<template lang="pug">
  v-layout.container.column
    canvas.reporting-chart(id="reportChart" width="1000" height="300" ref="canvas")
    v-layout.chart-bottom-container(align-center, justify-start, row, fill-height)
      p.chart-bottom-labels {{ this.bottomLabels.left }}
      v-spacer.grow.chart-bottom-spacer
      p.chart-bottom-labels {{ this.bottomLabels.right }}

  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { format } from 'date-fns';
import {
  SELECT,
  COMPARISON,
  PRIMARY_GRAPH_COLOR,
  PRIMARY_GRAPH_COLOR_COMPARATIVE,
  SECONDARY_GRAPH_COLOR,
  SECONDARY_GRAPH_COLOR_COMPARATIVE,
  BORDER_WIDTH,
  POINT_BACKGROUND_COLOR,
  POINT_RADIUS,
  HOURLY,
  DAILY,
  WEEKLY,
  HOURLY_FORMAT,
  DAILY_FORMAT,
  WEEKLY_FORMAT,
  MONTHLY_FORMAT,
  WEEK_OF,
  ALL_TIME,
  CURRENT_DAY,
} from '@/Reporting/config/constants';

let reportChart;

const dataset = {
  label: '',
  data: '',
  borderWidth: BORDER_WIDTH,
  pointBackgroundColor: POINT_BACKGROUND_COLOR,
  pointRadius: POINT_RADIUS,
};

export default {
  props: {
    headers: {
      type: Array,
      default: () => [],
    },
    graphData: {
      type: Array,
      default: () => [],
    },
    selectedDateRange: {
      type: String,
      default: '',
    },
    selectedDropdownLeft: {
      type: String,
      default: '',
    },
    selectedDropdownRight: {
      type: String,
      default: '',
    },
    selectedGraphInterval: {
      type: String,
      default: '',
    },
    includeComparedPercentage: {
      type: Boolean,
      default: false,
    },
    isGraphLoading: {
      type: Boolean,
      default: false,
    },
    responseIfNoData: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chart: null,
      chartLabels: [],
      datasetsToBeDisplayed: [],
      dataset1: {
        ...dataset,
        borderColor: PRIMARY_GRAPH_COLOR,
        main: 'main',
      },
      dataset2: {
        ...dataset,
        borderColor: SECONDARY_GRAPH_COLOR,
        main: 'main',
      },
      compareToDataset1: {
        ...dataset,
        borderColor: PRIMARY_GRAPH_COLOR_COMPARATIVE,
        comparison: 'comparison',
      },
      compareToDataset2: {
        ...dataset,
        borderColor: SECONDARY_GRAPH_COLOR_COMPARATIVE,
        comparison: 'comparison',
      },
      bottomLabels: {},
      tooltipTitles: [],
      tooltipComparisonTitles: [],
    };
  },
  watch: {
    graphData(newValue, oldValue) {
      if (newValue === oldValue || !newValue.length) return;

      this.setTooltipTitles();
      this.setTooltipComparisonTitles();
      this.setDatasetsToBeDisplayed();
      this.setChartLabels();
      this.getTimeIntervals();

      this.renderChart();
    },
  },
  methods: {
    setTooltipTitles() {
      const getTimestring = this.graphData[0].search.timeString;

      const datesArr = getTimestring.reduce((acc, val) => {
        const getDate = new Date(val);
        const formattedDates = {
          dateHourly: format(getDate, HOURLY_FORMAT),
          dateDaily: format(getDate, DAILY_FORMAT),
          dateWeekly: `${WEEK_OF} ${format(getDate, WEEKLY_FORMAT)}`,
          dateMonth: format(getDate, MONTHLY_FORMAT),
        };

        return [...acc, formattedDates];
      }, []);

      if (this.selectedGraphInterval === HOURLY) {
        this.tooltipTitles = datesArr.map(val => val.dateHourly);

        return;
      } else if (this.selectedGraphInterval === DAILY) {
        this.tooltipTitles = datesArr.map(val => val.dateDaily);

        return;
      } else if (this.selectedGraphInterval === WEEKLY) {
        this.tooltipTitles = datesArr.map(val => val.dateWeekly);

        return;
      }
      this.tooltipTitles = datesArr.map(val => val.dateMonth);
    },
    setTooltipComparisonTitles() {
      const hasComparison = this.graphData.find(c => c.comparison);
      if (!hasComparison) return;

      const getTimestring = this.graphData[0].comparison.timeString;

      const datesArr = getTimestring.reduce((acc, val) => {
        const getDate = new Date(val);
        const formattedDates = {
          dateHourly: format(getDate, HOURLY_FORMAT),
          dateDaily: format(getDate, DAILY_FORMAT),
          dateWeekly: `${WEEK_OF} ${format(getDate, WEEKLY_FORMAT)}`,
          dateMonth: format(getDate, MONTHLY_FORMAT),
        };

        return [...acc, formattedDates];
      }, []);

      if (this.selectedGraphInterval === HOURLY) {
        this.tooltipComparisonTitles = datesArr.map(val => val.dateHourly);

        return;
      } else if (this.selectedGraphInterval === DAILY) {
        this.tooltipComparisonTitles = datesArr.map(val => val.dateDaily);

        return;
      } else if (this.selectedGraphInterval === WEEKLY) {
        this.tooltipComparisonTitles = datesArr.map(val => val.dateWeekly);

        return;
      }
      this.tooltipComparisonTitles = datesArr.map(val => val.dateMonth);
    },
    setChartLabels() {
      // will determine the number of points to be displayed
      // on the chart. The length of the array = the number
      // of points in the line.
      let mapFunc = d => d.search.data;

      const hasComparison = this.graphData.find(c => c.comparison);
      if (hasComparison) {
        mapFunc = d => d.search.data && d.comparison.data;
      }

      const mapDataArrays = this.graphData.map(mapFunc);
      const arrOfLengths = mapDataArrays.map(d => d.length);
      const longestDataset = Math.max(...arrOfLengths);

      const generateArrayWithMaxLength = Array(longestDataset).fill('');
      this.chartLabels = generateArrayWithMaxLength;
    },
    getTimeIntervals() {
      if (this.selectedDateRange === CURRENT_DAY || this.selectedDateRange === ALL_TIME) {
        const filterTimestrings = this.graphData
          .filter(data => data.search.timeString || data.comparison.timeString)
          .reduce((acc, val) => {
            return [...acc, val.search.timeString];
          }, [])[0];

        const getFirstDate = new Date(filterTimestrings[0]);
        const getSecondDate = new Date(filterTimestrings[filterTimestrings.length - 1]);
        const formattedDates = {
          firstDateHourly: format(getFirstDate, HOURLY_FORMAT),
          firstDateDaily: format(getFirstDate, DAILY_FORMAT),
          firstDateWeekly: format(getFirstDate, WEEKLY_FORMAT),
          firstDateMonth: format(getFirstDate, MONTHLY_FORMAT),
          secondDateHourly: format(getSecondDate, HOURLY_FORMAT),
          secondDateDaily: format(getSecondDate, DAILY_FORMAT),
          secondDateWeekly: format(getSecondDate, WEEKLY_FORMAT),
          secondDateMonth: format(getSecondDate, MONTHLY_FORMAT),
        };

        this.checkDateIntervalToSetBottomLabels(formattedDates);
        return;
      }

      const findDateRangeFromResponse = this.graphData.find(d => d.search.dateRange);

      const getFirstDate = new Date(findDateRangeFromResponse.search.dateRange[0]);
      const getSecondDate = new Date(findDateRangeFromResponse.search.dateRange[1]);
      const formattedDates = {
        firstDateHourly: format(getFirstDate, HOURLY_FORMAT),
        firstDateDaily: format(getFirstDate, DAILY_FORMAT),
        firstDateWeekly: format(getFirstDate, WEEKLY_FORMAT),
        firstDateMonth: format(getFirstDate, MONTHLY_FORMAT),
        secondDateHourly: format(getSecondDate, HOURLY_FORMAT),
        secondDateDaily: format(getSecondDate, DAILY_FORMAT),
        secondDateWeekly: format(getSecondDate, WEEKLY_FORMAT),
        secondDateMonth: format(getSecondDate, MONTHLY_FORMAT),
      };

      this.checkDateIntervalToSetBottomLabels(formattedDates);
    },
    checkDateIntervalToSetBottomLabels(formattedDates) {
      if (this.selectedGraphInterval === HOURLY) {
        this.setsBottomLabelsForHourlyInterval(formattedDates);
      } else if (this.selectedGraphInterval === DAILY) {
        this.setsBottomLabelsForDailyInterval(formattedDates);
      } else if (this.selectedGraphInterval === WEEKLY) {
        this.setsBottomLabelsForWeeklyInterval(formattedDates);
      } else {
        this.setsBottomLabelsForMonthlyInterval(formattedDates);
      }
    },
    setsBottomLabelsForHourlyInterval(formattedDates) {
      this.bottomLabels = {
        left: formattedDates.firstDateHourly,
        right: formattedDates.secondDateHourly,
      };
    },
    setsBottomLabelsForDailyInterval(formattedDates) {
      this.bottomLabels = {
        left: formattedDates.firstDateDaily,
        right: formattedDates.secondDateDaily,
      };
    },
    setsBottomLabelsForWeeklyInterval(formattedDates) {
      this.bottomLabels = {
        left: `${WEEK_OF} ${formattedDates.firstDateWeekly}`,
        right: `${WEEK_OF} ${formattedDates.secondDateWeekly}`,
      };
    },
    setsBottomLabelsForMonthlyInterval(formattedDates) {
      this.bottomLabels = {
        left: formattedDates.firstDateMonth,
        right: formattedDates.secondDateMonth,
      };
    },
    setDatasetsToBeDisplayed() {
      const hasComparison = this.graphData.find(c => c.comparison);
      const leftEventValueToEventText = this.headers.find(h => h.text === this.selectedDropdownLeft)
        .value;

      const matchToLeftDropdown = this.graphData.find(
        v => v.search.eventType === leftEventValueToEventText,
      );

      const dataset1Label = this.headers.find(h => h.text === this.selectedDropdownLeft).text;
      matchToRightDropdown;

      let rightEventValueToEventText;
      let matchToRightDropdown;
      let dataset2Label;
      if (this.selectedDropdownRight !== SELECT) {
        rightEventValueToEventText = this.headers.find(h => h.text === this.selectedDropdownRight)
          .value;
        matchToRightDropdown = this.graphData.find(
          v => v.search.eventType === rightEventValueToEventText,
        );
        dataset2Label = this.headers.find(h => h.text === this.selectedDropdownRight).text;
      }
      if (hasComparison) {
        const matchToLeftComparison = this.graphData.find(
          v => v.comparison.eventType === leftEventValueToEventText,
        );

        const matchToRightComparison = this.graphData.find(
          v => v.comparison.eventType === rightEventValueToEventText,
        );

        if (this.graphData.length === 1) {
          this.dataset1.label = dataset1Label;
          this.dataset1.data = matchToLeftDropdown.search.data;

          this.compareToDataset1.label = `${dataset1Label} ${COMPARISON}`;
          this.compareToDataset1.data = matchToLeftComparison.comparison.data;

          this.datasetsToBeDisplayed = [this.dataset1, this.compareToDataset1];

          return;
        }

        this.dataset1.label = dataset1Label;
        this.dataset1.data = matchToLeftDropdown.search.data;

        this.compareToDataset1.label = `${dataset1Label} ${COMPARISON}`;
        this.compareToDataset1.data = matchToLeftComparison.comparison.data;

        this.dataset2.label = dataset2Label;
        this.dataset2.data = matchToRightDropdown.search.data;

        this.compareToDataset2.label = `${dataset2Label} ${COMPARISON}`;
        this.compareToDataset2.data = matchToRightComparison.comparison.data;

        this.datasetsToBeDisplayed = [
          this.dataset1,
          this.dataset2,
          this.compareToDataset1,
          this.compareToDataset2,
        ];

        return;
      }

      if (this.graphData.length === 1) {
        this.dataset1.label = dataset1Label;
        this.dataset1.data = matchToLeftDropdown.search.data;

        this.datasetsToBeDisplayed = [this.dataset1];
        return;
      }

      this.dataset1.label = dataset1Label;
      this.dataset1.data = matchToLeftDropdown.search.data;

      this.dataset2.label = dataset2Label;
      this.dataset2.data = matchToRightDropdown.search.data;

      this.datasetsToBeDisplayed = [this.dataset1, this.dataset2];
      matchToRightDropdown;
    },
    renderChart() {
      if (typeof reportChart !== 'undefined') reportChart.destroy();

      const datasets = [...this.datasetsToBeDisplayed];
      const chartTooltipTitles = [...this.tooltipTitles];
      const chartTooltipComparisonTitles = [...this.tooltipComparisonTitles];
      const leftDropdown = this.selectedDropdownLeft;
      const rightDropdown = this.selectedDropdownRight;
      const isCompared = this.includeComparedPercentage;

      reportChart = new Chart(document.getElementById('reportChart').getContext('2d'), {
        type: 'line',
        data: {
          labels: this.chartLabels,
          datasets: this.datasetsToBeDisplayed,
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: 'nearest',
              external: function (context) {
                // Tooltip Element
                let tooltipEl = document.getElementById('chartjs-tooltip');

                // Create element on first render
                if (!tooltipEl) {
                  tooltipEl = document.createElement('div');
                  tooltipEl.id = 'chartjs-tooltip';
                  tooltipEl.innerHTML = '<table></table>';
                  document.body.appendChild(tooltipEl);
                }

                // Hide if no tooltip
                let tooltipModel = context.tooltip;
                if (tooltipModel.opacity === 0) {
                  tooltipEl.style.opacity = 0;
                  return;
                }

                function getBody(bodyItem) {
                  return bodyItem.lines;
                }

                if (tooltipModel.body) {
                  let index = reportChart.tooltip.dataPoints[0].dataIndex;

                  const title = `<tr class='tooltip-top-half' style="margin-bottom: 10px">
                  <th class='tooltip-title' style="font-size: 12px;text-align: left;">${chartTooltipTitles[index]}</th>
                  </tr>`;

                  let dataLeft;
                  if (datasets[0].data[index] === null) {
                    dataLeft = '';
                  } else {
                    dataLeft = `<tr>
                  <td class="tooltip-dataset" style="display: flex; flex-direction: row; align-items: center;justify-content: flex-start">
                  <i class="material-icons" style="color: ${PRIMARY_GRAPH_COLOR}; transform: scale(1.2)">stop</i>
                  <span style="font-size: 12px;margin-right: 5px">${leftDropdown}:  ${datasets[0].data[index]}</span>
                  </td>
                  </tr>`;
                  }

                  let dataRight;

                  if (rightDropdown !== SELECT) {
                    const rightEvent = datasets.find(d => d.label === rightDropdown);

                    if (rightEvent.data[index] === null) {
                      dataRight = '';
                    } else {
                      dataRight = `<tr>
                    <td class="tooltip-dataset" style="display: flex; flex-direction: row; align-items: center;justify-content: flex-start">
                    <i class="material-icons" style="color: ${SECONDARY_GRAPH_COLOR}; transform: scale(1.2)">stop</i>
                    <span style="font-size: 12px;margin-right: 5px;">${rightDropdown}:  ${rightEvent.data[index]}</span>
                    </td>
                    </tr>`;
                    }
                  }

                  let comparisonTitle;
                  let dataLeftComparison;
                  let dataRightComparison;

                  if (isCompared) {
                    comparisonTitle = `<tr class='tooltip-bottom-half' style="margin: 10px, 0, 5px, 0">
                    <th class='tooltip-title' style="font-size: 12px;text-align: left;">${chartTooltipComparisonTitles[index]}</th>
                    </tr>`;

                    const leftComparison = datasets.find(
                      d => d.label === `${leftDropdown} ${COMPARISON}`,
                    );

                    dataLeftComparison = `<tr>
                    <td class="tooltip-dataset" style="display: flex; flex-direction: row; align-items: center;justify-content: flex-start">
                    <i class="material-icons" style="color: ${PRIMARY_GRAPH_COLOR_COMPARATIVE}; transform: scale(1.2)">stop</i>
                    <span style="font-size: 12px;margin-right: 5px">${leftDropdown}:  ${leftComparison.data[index]}</span>
                    </td>
                    </tr>`;

                    if (rightDropdown !== SELECT) {
                      dataRightComparison = `<tr>
                      <td class="tooltip-dataset" style="display: flex; flex-direction: row; align-items: center;justify-content: flex-start">
                      <i class="material-icons" style="color: ${SECONDARY_GRAPH_COLOR_COMPARATIVE}; transform: scale(1.2)">stop</i>
                      <span style="font-size: 12px;margin-right: 5px">${rightDropdown}:  ${datasets[3].data[index]}</span>
                      </td>
                      </tr>`;
                    }
                  }

                  let tableRoot = tooltipEl.querySelector('table');
                  let innerHtml;

                  if (leftDropdown && rightDropdown === SELECT && !isCompared) {
                    innerHtml = `<tbody">
                    ${title}
                    ${dataLeft}
                    </tbody>`;

                    tableRoot.innerHTML = innerHtml;
                  } else if (leftDropdown && rightDropdown === SELECT && isCompared) {
                    innerHtml = `<tbody">
                    ${title}
                    ${dataLeft}
                    ${comparisonTitle}
                    ${dataLeftComparison}
                    </tbody>`;

                    tableRoot.innerHTML = innerHtml;
                  } else if (leftDropdown && rightDropdown !== SELECT && !isCompared) {
                    innerHtml = `<tbody">
                    ${title}
                    ${dataLeft}
                    ${dataRight}
                    </tbody>`;

                    tableRoot.innerHTML = innerHtml;
                  } else {
                    innerHtml = `<tbody">
                    ${title}
                    ${dataLeft}
                    ${dataRight}
                    ${comparisonTitle}
                    ${dataLeftComparison}
                    ${dataRightComparison}
                    </tbody>`;

                    tableRoot.innerHTML = innerHtml;
                  }
                }

                let position = context.chart.canvas.getBoundingClientRect();

                const { offsetLeft: positionX, offsetTop: positionY } = context.chart.canvas;

                // Display, position, and set styles for font
                tooltipEl.style.opacity = 1;
                tooltipEl.style.position = 'absolute';
                tooltipEl.style.zIndex = 9999;

                let index = reportChart.tooltip.dataPoints[0].dataIndex;
                if (chartTooltipTitles.length === index + 1) {
                  tooltipEl.style.left = position.left + tooltipModel.caretX - 80 + 'px';
                } else {
                  tooltipEl.style.left = position.left + tooltipModel.caretX + 'px';
                }

                tooltipEl.style.top =
                  position.top + window.pageYOffset + tooltipModel.caretY + 'px';
                tooltipEl.style.padding = '4px';
                tooltipEl.style.pointerEvents = 'none';
                tooltipEl.style.backgroundColor = 'rgba(232, 232, 235, 0.8)';
                tooltipEl.style.borderRadius = '5px';
                tooltipEl.style.color = PRIMARY_GRAPH_COLOR;
                tooltipEl.style.height = 'auto';
                tooltipEl.style.fontFamily = 'Nunito, sans-serif';
                tooltipEl.style.fontWeight = 'bold';
                tooltipEl.style.boxShadow = '3px 3px 5px rgba(0, 0, 0, 0.2)';
              },
            },
          },
          scales: {
            x: {
              display: false,
            },
          },
        },
      });
    },
  },
  mounted() {
    this.setTooltipTitles();
    this.setTooltipComparisonTitles();
    this.setDatasetsToBeDisplayed();
    this.setChartLabels();
    this.getTimeIntervals();
    this.renderChart();
  },
};
</script>

<style lang="scss" scoped>
.container {
  padding: 0 20px;
  height: 100%;
  width: 100%;
  margin-top: 30px;
}
.reporting-chart {
  max-height: 450px;
}
.chart-bottom-container {
  margin-top: 12px;
}
</style>
